import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import WebApp from '@twa-dev/sdk';
import AuthContext from "../context/auth.context";
import useAuth from "../hooks/auth.hook";
import ProviderRoutes from "./ProviderRoutes";
import useHttp from "../hooks/http.hook";
import './App.css';


function getTelegramInitData() {
    try {
        return String(WebApp.initData || window.Telegram?.WebApp?.initData || "");
    } catch (_) {
        return "";
    }
}

function setupTelegramWebApp() {
    const calls = [
        () => WebApp.ready(),
        () => WebApp.expand(),
        () => WebApp.disableVerticalSwipes(),
        () => WebApp.enableClosingConfirmation(),
        () => WebApp.requestFullscreen(),
    ];
    for (const call of calls) {
        try {
            call();
        } catch (_) {
            // Older Telegram clients may not support every WebApp method.
        }
    }
}

function Spinner() {
    return (
        <div className="app-spinnerWrap">
            <svg width="56" height="56" viewBox="0 0 50 50" aria-label="Loading" role="status">
                <circle
                    cx="25"
                    cy="25"
                    r="20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeDasharray="31.415, 31.415"
                >
                    <animateTransform
                        attributeName="transform"
                        type="rotate"
                        from="0 25 25"
                        to="360 25 25"
                        dur="1s"
                        repeatCount="indefinite"
                    />
                </circle>
            </svg>
        </div>
    );
}

function AuthError({ message, onRetry }) {
    return (
        <div className="app-authError">
            <p>{message}</p>
            <button type="button" className="app-authErrorButton" onClick={onRetry}>
                Повторить
            </button>
        </div>
    );
}

function App() {
    const navigate = useNavigate();
    const { token, login, logout, ready } = useAuth();
    const isAuthenticated = !!token;
    
    const { requestWithMeta } = useHttp();
    const authContextValue = useMemo(
        () => ({ token, login, logout, isAuthenticated }),
        [token, login, logout, isAuthenticated]
    );

    const [authLoading, setAuthLoading] = useState(true);
    const [authError, setAuthError] = useState(null);
    const [telegramAttempt, setTelegramAttempt] = useState(0);

    useEffect(() => {
        // On mobile browsers/WebViews (incl. Telegram), `position: fixed` footers
        // often jump above the software keyboard. We hide fixed footers while
        // the keyboard is open so actions stay "below" it (off-screen).
        const root = document.documentElement;

        const isCoarsePointer =
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(pointer: coarse)').matches;

        const isSmallViewport =
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(max-width: 900px)').matches;

        const shouldHandle = isCoarsePointer || isSmallViewport;
        if (!shouldHandle) return;

        const isTextLikeInput = (el) => {
            if (!el) return false;
            if (el.isContentEditable) return true;
            const tag = String(el.tagName || '').toLowerCase();
            if (tag === 'textarea' || tag === 'select') return true;
            if (tag !== 'input') return false;

            const type = String(el.getAttribute('type') || 'text').toLowerCase();
            // Inputs that typically don't open the soft keyboard:
            const nonKeyboardTypes = new Set([
                'button',
                'submit',
                'reset',
                'checkbox',
                'radio',
                'range',
                'file',
                'color',
                'image',
                'hidden',
            ]);
            if (nonKeyboardTypes.has(type)) return false;
            return !el.disabled && !el.readOnly;
        };

        const setKeyboardOpen = (open) => {
            root.classList.toggle('provider-keyboard-open', !!open);
        };

        const onFocusIn = (e) => {
            if (isTextLikeInput(e.target)) setKeyboardOpen(true);
        };

        const onFocusOut = () => {
            // Delay so `document.activeElement` is updated.
            setTimeout(() => {
                const stillFocused = isTextLikeInput(document.activeElement);
                if (!stillFocused) setKeyboardOpen(false);
            }, 0);
        };

        document.addEventListener('focusin', onFocusIn);
        document.addEventListener('focusout', onFocusOut);

        const vv = window.visualViewport;
        let detachVv = null;
        if (vv && typeof vv.addEventListener === 'function') {
            const onVvChange = () => {
                // When keyboard opens, the *visual* viewport shrinks.
                const keyboardPx = window.innerHeight - vv.height;
                const keyboardOpen = keyboardPx > 140; // heuristic threshold
                if (keyboardOpen) {
                    setKeyboardOpen(true);
                } else if (!isTextLikeInput(document.activeElement)) {
                    setKeyboardOpen(false);
                }
            };

            vv.addEventListener('resize', onVvChange);
            vv.addEventListener('scroll', onVvChange);
            onVvChange();

            detachVv = () => {
                vv.removeEventListener('resize', onVvChange);
                vv.removeEventListener('scroll', onVvChange);
            };
        }

        return () => {
            document.removeEventListener('focusin', onFocusIn);
            document.removeEventListener('focusout', onFocusOut);
            if (detachVv) detachVv();
            setKeyboardOpen(false);
        };
    }, []);

    useEffect(() => {
        if (!ready) return undefined;

        const initDataRaw = getTelegramInitData();
        if (!initDataRaw || token) {
            setAuthLoading(false);
            return undefined;
        }

        // One Telegram sign-in per attempt. A later 401 must not mint tokens forever.
        if (telegramAttempt > 0) {
            setAuthLoading(false);
            setAuthError((current) => current || "Сессия истекла. Откройте приложение через Telegram-бот.");
            return undefined;
        }

        let cancelled = false;

        const fetchAuth = async () => {
            setAuthLoading(true);
            setAuthError(null);
            try {
                try {
                    WebApp.ready();
                } catch (_) {
                    // initData may still be available without ready().
                }

                const result = await requestWithMeta("/operator/auth", "POST", { initDataRaw });
                if (cancelled) return;
                if (result?.status === 403) {
                    logout();
                    navigate("/account-inactive", { replace: true });
                    return;
                }
                if (result?.data?.token) {
                    login(result.data.token);
                    return;
                }
                setAuthError("Не удалось войти. Откройте приложение через Telegram-бот.");
            } catch (_) {
                if (!cancelled) {
                    setAuthError("Не удалось войти. Откройте приложение через Telegram-бот.");
                }
            } finally {
                if (!cancelled) {
                    setTelegramAttempt((count) => count + 1);
                    setAuthLoading(false);
                }
            }
        };

        fetchAuth();

        return () => {
            cancelled = true;
        };
    }, [ready, token, login, logout, requestWithMeta, navigate, telegramAttempt]);

    useEffect(() => {
        setupTelegramWebApp();
    }, []);

    const retryAuth = () => {
        setAuthError(null);
        setAuthLoading(true);
        setTelegramAttempt(0);
    };

    if (!ready || authLoading) {
        return (
            <div className="app">
                <Spinner />
            </div>
        );
    }

    if (!isAuthenticated) {
        if (!getTelegramInitData()) {
            return <Navigate to="/login" replace />;
        }

        return (
            <div className="app">
                {authError ? <AuthError message={authError} onRetry={retryAuth} /> : <Spinner />}
            </div>
        );
    }

    const app = (
        <AuthContext.Provider value={authContextValue}>
            <ProviderRoutes />
        </AuthContext.Provider>
    );

    return <div className="app">{app}</div>;
}

export default App;